import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { contactFormContent } from '../data/mock';
import { submitContactForm } from '../lib/api';
import { Send, CheckCircle, Loader2 } from 'lucide-react';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    message: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await submitContactForm(formData);

      setIsSuccess(true);
      setFormData({
        name: '',
        organization: '',
        email: '',
        phone: '',
        message: '',
        consent: false
      });
    } catch (err) {
      setError(err.message || 'Kļūda nosūtot pieprasījumu. Lūdzu, mēģiniet vēlreiz.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section className="py-20 lg:py-32 bg-white" id="contact">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-emerald-50 rounded-2xl p-12">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              {contactFormContent.successMessage}
            </h3>
            <p className="text-slate-600">
              Mēs sazināsimies ar jums tīklīš.
            </p>
            <Button
              onClick={() => setIsSuccess(false)}
              variant="outline"
              className="mt-6"
            >
              Nosūtīt vēl vienu pieprasījumu
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-32 bg-white" id="contact">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            {contactFormContent.title}
          </h2>
          <div className="w-16 h-1 bg-slate-800 mx-auto" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium text-slate-700">
                Vārds, uzvārds *
              </label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Jānis Bērziņš"
                className="bg-slate-50 border-slate-200 focus:border-slate-400"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="organization" className="text-sm font-medium text-slate-700">
                Organizācija *
              </label>
              <Input
                id="organization"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                required
                placeholder="Pašvaldība/Uzņēmums"
                className="bg-slate-50 border-slate-200 focus:border-slate-400"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-slate-700">
                E-pasts *
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="janis@organizacija.lv"
                className="bg-slate-50 border-slate-200 focus:border-slate-400"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="phone" className="text-sm font-medium text-slate-700">
                Tālrunis
              </label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+371 20000000"
                className="bg-slate-50 border-slate-200 focus:border-slate-400"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="message" className="text-sm font-medium text-slate-700">
              Ziņa *
            </label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={5}
              placeholder="Aprakstiet savu projektu vai jautājumu..."
              className="bg-slate-50 border-slate-200 focus:border-slate-400 resize-none"
            />
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="consent"
              name="consent"
              checked={formData.consent}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent: checked }))}
              required
              className="mt-1"
            />
            <label htmlFor="consent" className="text-sm text-slate-600">
              Piekrītu, ka mans pieprasījums tiek apstrādāts sazinai. *
            </label>
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting || !formData.consent}
            className="w-full bg-slate-800 hover:bg-slate-700 text-white py-6 text-lg"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Nosūta...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                {contactFormContent.submitButton}
              </>
            )}
          </Button>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
